/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Container } from "@/components/ui-kit";
import { submitConceptCorrectionRequest } from "@/lib/api";

const REQUEST_TYPES = [
  { value: "claim_ownership", label: "I own this business — I want to claim this website" },
  { value: "request_correction", label: "Something on this concept is inaccurate — please correct it" },
  { value: "request_removal", label: "I want this concept removed" },
  { value: "dispute_accuracy", label: "I'm disputing how my business is represented" },
];

export default function ConceptCorrectionRequest() {
  const [params] = useSearchParams();
  const slug = params.get("slug") || "";
  const business = params.get("business") || "";
  const [form, setForm] = useState({
    website_slug: slug, business_name: business, requester_name: "", requester_email: "",
    request_type: "request_correction", message: "",
  });
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    try {
      await submitConceptCorrectionRequest(form);
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <Container className="py-24 max-w-xl">
        <p data-testid="concept-correction-success" className="text-lg">
          Thank you — your request has been received. MySiteCare will review it and get back to you. This is free and separate from purchasing the website.
        </p>
      </Container>
    );
  }

  return (
    <Container className="py-16 max-w-xl">
      <h1 className="text-2xl font-bold mb-2">Are you the business owner?</h1>
      <p className="text-sm text-[#475569] mb-8" data-testid="concept-correction-free-notice">
        Claim, correct, or request removal of this concept website — free, and separate from buying it.
      </p>
      <form onSubmit={submit} className="space-y-4" data-testid="concept-correction-form">
        <input required data-testid="concept-correction-name" placeholder="Your name" value={form.requester_name}
          onChange={(e) => setForm({ ...form, requester_name: e.target.value })}
          className="w-full border rounded-lg px-4 py-2" />
        <input required type="email" data-testid="concept-correction-email" placeholder="Your email" value={form.requester_email}
          onChange={(e) => setForm({ ...form, requester_email: e.target.value })}
          className="w-full border rounded-lg px-4 py-2" />
        <input required data-testid="concept-correction-business" placeholder="Business name" value={form.business_name}
          onChange={(e) => setForm({ ...form, business_name: e.target.value })}
          className="w-full border rounded-lg px-4 py-2" />
        <select data-testid="concept-correction-type" value={form.request_type}
          onChange={(e) => setForm({ ...form, request_type: e.target.value })}
          className="w-full border rounded-lg px-4 py-2">
          {REQUEST_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <textarea data-testid="concept-correction-message" placeholder="Details (optional)" value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          className="w-full border rounded-lg px-4 py-2" rows={4} />
        <button type="submit" disabled={status === "submitting"} data-testid="concept-correction-submit"
          className="w-full rounded-lg bg-[#0f172a] text-white py-3 font-semibold disabled:opacity-50">
          {status === "submitting" ? "Sending…" : "Send request"}
        </button>
        {status === "error" && <p className="text-red-600 text-sm" data-testid="concept-correction-error">Something went wrong — please try again.</p>}
      </form>
    </Container>
  );
}
